import { useEffect, useMemo, useState } from 'react';
import { TreeSelect } from 'antd';
import {
  useGetNodeTreeQuery,
  type OrgNode,
} from '../../../features/organizations/organizationsApi';
import { nodeKey } from '../../../components/OrgNodeTree';

/**
 * Org-node hierarchy picker — an antd TreeSelect over the organization's node tree
 * (GET /organisation/node/tree/), single-select with **server-side** search (`?search=`).
 * The value is the org-node key ("n:15"); `onChange` also returns the node's display label.
 */
/** Whether a node value exists anywhere in the tree. */
type TSNode = { value: string; title: string; children?: TSNode[] };
const hasValue = (nodes: TSNode[], v: string): boolean =>
  nodes.some((n) => n.value === v || (n.children ? hasValue(n.children, v) : false));

export default function PartyLocationDropdown({
  organization,
  value,
  placeholder = 'Select Levels',
  invalid,
  kind,
  onChange,
}: {
  organization?: number;
  value: string;
  placeholder?: string;
  invalid?: boolean;
  /** Load a specific hierarchy (e.g. "police", "revenue") instead of the default node tree. */
  kind?: string;
  /** `label` is the node's display name (for callers that need it, e.g. a card breadcrumb). */
  onChange: (key: string, label?: string) => void;
}) {
  // Debounce the typed text into the backend node-tree search.
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: orgTree, isFetching } = useGetNodeTreeQuery(
    { organization: organization as number, ...(kind ? { kind } : {}), ...(search ? { search } : {}) },
    { skip: organization == null },
  );

  // Map the node hierarchy to antd TreeSelect data (value = "n:<id>" selection key).
  const treeData = useMemo(() => {
    const toNode = (n: OrgNode): TSNode => {
      const kids = (n.children ?? []).map(toNode);
      return {
        value: nodeKey(Number(n.id)),
        title:
          [n.org_name, n.level_name].filter(Boolean).join(' - ') || n.org_name || `Node ${n.id}`,
        ...(kids.length ? { children: kids } : {}),
      };
    };
    const base = (orgTree ?? []).map(toNode);
    // A preset value not yet in the tree — show "Loading…" while the tree fetches (it resolves
    // to the real node once loaded), avoiding a flash of the raw node id.
    if (value && !hasValue(base, value)) {
      base.unshift({ value, title: isFetching ? 'Loading…' : value });
    }
    return base;
  }, [orgTree, value, isFetching]);

  return (
    <TreeSelect
      className="w-full"
      placeholder={organization == null ? 'Select an organization first' : placeholder}
      disabled={organization == null}
      showSearch
      // Server-side search: don't filter locally; the node-tree query returns matches.
      filterTreeNode={false}
      searchValue={query}
      onSearch={setQuery}
      onOpenChange={(open) => {
        if (!open) setQuery('');
      }}
      loading={isFetching}
      notFoundContent={isFetching ? 'Searching…' : 'No matches'}
      treeDefaultExpandAll
      allowClear
      status={invalid ? 'error' : undefined}
      value={value || undefined}
      treeData={treeData}
      onChange={(key, labelList) => {
        const label = Array.isArray(labelList) ? String(labelList[0] ?? '') : '';
        onChange((key as string) || '', label);
      }}
    />
  );
}
